import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../../components/feature/Header';
import { Footer } from '../../components/feature/Footer';
import BookingForm from '../tours-view/BookingForm';
import experiencesService from '@/services/experiences';
import { useCart } from '../../hooks/useCart';
import { getExperienceImageUrl } from '@/environments/environment';

const ExperienceDetailsPage: React.FC = () => {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const id = params.get('id') || '';
    const location = params.get('location') || '';

    const [experience, setExperience] = useState<any | undefined>(undefined);
    const [loading, setLoading] = useState(true);

    // Fetch experience by id
    useEffect(() => {
        let mounted = true;
        const load = async () => {
            if (!id) {
                if (mounted) setLoading(false);
                return;
            }
            try {
                const fetched = await experiencesService.getExperience(id);
                // normalize id field
                if (fetched) {
                    fetched.id = fetched.id || fetched._id;
                }
                if (mounted) setExperience(fetched || undefined);
            } catch (e) {
                console.error('Failed to load experience', e);
                if (mounted) setExperience(undefined);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        load();
        return () => { mounted = false; };
    }, [id]);

    const { cartItems, addToCart, removeFromCart, updateQuantity } = useCart();

    // If experience is found and not present in cart, add it
    useEffect(() => {
        if (!experience) return;
        const expId = `exp-${(experience as any).id || (experience as any)._id}`;
        const existing = cartItems.find(ci => ci.id === expId);
        if (!existing) {
            const image = getExperienceImageUrl(experience.imageUrl) || '';
            addToCart({
                id: expId,
                title: experience.title,
                price: experience.price || 0,
                image,
                category: experience.category,
                description: experience.description,
                itinerary: experience.itinerary || [],
                location: location || experience.location
            });
        }
    }, [experience, cartItems, addToCart, location]);

    const cartItem = experience ? cartItems.find(ci => ci.id === `exp-${(experience as any).id || (experience as any)._id}`) : undefined;
    const [level, setLevel] = useState<0 | 1 | 2>(0);
    const descRef = useRef<HTMLDivElement | null>(null);
    const itinRef = useRef<HTMLDivElement | null>(null);
    const [maxHeight, setMaxHeight] = useState<number>(0);

    // measure heights when level changes
    useEffect(() => {
        const shortH = descRef.current ? descRef.current.scrollHeight : 0;
        const itinH = itinRef.current ? itinRef.current.scrollHeight : 0;
        if (level === 0) setMaxHeight(0);
        else if (level === 1) setMaxHeight(shortH + 8);
        else setMaxHeight(shortH + itinH + 12);
    }, [level]);

    // Itinerary renderer
    const ItineraryAccordion: React.FC<{ itinerary: Array<any> }> = ({ itinerary }) => {
        if (!itinerary || itinerary.length === 0) return null;
        return (
            <div className="space-y-3">
                {itinerary.map((it: any, idx: number) => (
                    <div key={idx} className="p-2 bg-gray-50 rounded">
                        <div className="text-sm font-semibold text-gray-800">{it.title || `Day ${idx + 1}`}</div>
                        <div className="text-sm text-gray-600">{it.content || it.description || it.details || ''}</div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <main>
                <section className="py-16">
                    <div className="max-w-6xl mx-auto px-6">
                        {loading ? (
                            <div className="text-center py-16">
                                <h2 className="text-2xl font-bold">Loading...</h2>
                            </div>
                        ) : experience ? (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                <div className="lg:col-span-2 space-y-6">
                                    {/* Cart-style item display */}
                                    {cartItem && (
                                        <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                                            <div className="p-4">
                                                <div className="flex items-start gap-4">
                                                    <img
                                                        src={cartItem.image}
                                                        alt={cartItem.title}
                                                        className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                        }}
                                                    />
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <div>
                                                                <h4 className="font-medium text-gray-900 text-sm line-clamp-2">{cartItem.title}</h4>
                                                                <p className="text-xs text-gray-500">{cartItem.category}</p>
                                                                {cartItem.location && <p className="text-xs text-gray-500">📍 {cartItem.location}</p>}
                                                            </div>
                                                            <div className="text-sm font-semibold text-emerald-600">₹{(cartItem.price * cartItem.quantity).toLocaleString()}</div>
                                                        </div>

                                                        <div className="mt-2 flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => updateQuantity(cartItem.id, Math.max(1, cartItem.quantity - 1))}
                                                                    className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm"
                                                                >
                                                                    -
                                                                </button>
                                                                <span className="text-sm font-medium">{cartItem.quantity}</span>
                                                                <button
                                                                    onClick={() => updateQuantity(cartItem.id, cartItem.quantity + 1)}
                                                                    className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded text-sm"
                                                                >
                                                                    +
                                                                </button>
                                                            </div>

                                                            <div className="flex items-center gap-2">
                                                                <button
                                                                    onClick={() => removeFromCart(cartItem.id)}
                                                                    className="text-red-500 hover:text-red-700 p-2"
                                                                >
                                                                    <i className="ri-delete-bin-line text-xl"></i>
                                                                </button>
                                                                {level === 0 && (
                                                                    <button
                                                                        onClick={() => setLevel(1)}
                                                                        className="bg-emerald-600 text-white px-3 py-1 rounded-md text-sm"
                                                                    >
                                                                        View
                                                                    </button>
                                                                )}
                                                                {level === 1 && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => setLevel(2)}
                                                                            className="bg-emerald-600 text-white px-3 py-1 rounded-md text-sm"
                                                                        >
                                                                            View More
                                                                        </button>
                                                                        <button
                                                                            onClick={() => setLevel(0)}
                                                                            className="ml-2 text-sm text-gray-600"
                                                                        >
                                                                            Hide
                                                                        </button>
                                                                    </>
                                                                )}
                                                                {level === 2 && (
                                                                    <>
                                                                        <button
                                                                            onClick={() => setLevel(0)}
                                                                            className="bg-gray-100 text-gray-800 px-3 py-1 rounded-md text-sm"
                                                                        >
                                                                            Collapse
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>

                                                        <div
                                                            style={{ maxHeight: `${maxHeight}px`, transition: 'max-height 350ms ease, opacity 250ms ease' }}
                                                            className="overflow-hidden mt-3"
                                                            aria-hidden={level === 0}
                                                        >
                                                            <div ref={descRef} className="text-sm text-gray-700 mb-2 opacity-100">
                                                                <p className="">{cartItem.description || 'No description available.'}</p>
                                                            </div>

                                                            <div ref={itinRef} className="mt-2">
                                                                {cartItem.itinerary && <ItineraryAccordion itinerary={cartItem.itinerary} />}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex justify-between">
                                        <button
                                            onClick={() => window.history.back()}
                                            className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-2"
                                        >
                                            <i className="ri-arrow-left-line"></i>
                                            Continue Browsing
                                        </button>
                                    </div>
                                </div>

                                <div className="lg:col-span-1">
                                    <BookingForm packageId={experience.id} packageTitle={experience.title} price={experience.price} />
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-16">
                                <h2 className="text-2xl font-bold">Experience not found</h2>
                                <p className="text-gray-600 mt-2">Please check the link or return to the experiences list.</p>
                                <button
                                    onClick={() => window.history.back()}
                                    className="mt-4 text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-2 justify-center"
                                >
                                    <i className="ri-arrow-left-line"></i>
                                    Go Back
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default ExperienceDetailsPage;
